/**
 * Service for AI Garden Image Analysis
 * Analyzes garden photos and returns structured data about detected features
 */

import type { AIAnalysis } from '@data/publicPlans';
import { buildApiUrl } from '@utils/appBase';

export interface AnalyzeGardenImageRequest {
	image_url: string;
	use_cache?: boolean;
	existing_style_key?: string; // Optional: if style is already set, filter style questions
	garden_uses?: string[]; // Optional: how user will use the garden (e.g. relaxing, kids_pets, hosting) — tailors analysis
	ui_language?: 'en' | 'de' | 'hr';
}

export interface AnalyzeGardenImageResponse {
	success: boolean;
	data?: AIAnalysis;
	error?: string;
	error_type?: string;
	error_message?: string;
}

/**
 * Analyze a garden image using AI
 * Returns detected features, size estimates, suggested questions, etc.
 * Pass styleKey and gardenUses when available so the AI can tailor the analysis.
 */
export async function analyzeGardenImage(
	imageUrl: string,
	useCache: boolean = true,
	existingStyleKey?: string,
	gardenUses?: string[],
	uiLanguage?: 'en' | 'de' | 'hr'
): Promise<AIAnalysis> {
	try {
		const response = await fetch(buildApiUrl('/analyze-garden-image'), {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({
				image_url: imageUrl,
				use_cache: useCache,
				existing_style_key: existingStyleKey,
				garden_uses: gardenUses && gardenUses.length > 0 ? gardenUses : undefined,
				ui_language: uiLanguage,
			}),
		});

		const data: AnalyzeGardenImageResponse = await response.json();

		if (!response.ok || !data.success) {
			const errorMessage = data.error || data.error_message || 'Failed to analyze garden image';
			throw new Error(errorMessage);
		}

		if (!data.data) {
			throw new Error('No analysis data returned');
		}

		return data.data;
	} catch (error) {
		console.error('Error analyzing garden image:', error);
		if (error instanceof Error) {
			throw error;
		}
		throw new Error('Failed to analyze garden image');
	}
}

